import { isDatalakeRecord } from '../../utils/partnerRecordOrigin';
import { type LocationListItemsType } from '../types';

const isSearchable = (item: LocationListItemsType) => {
  const isNBP = isDatalakeRecord(item.meta?.source);
  const isLocation = item.meta?.isLocation;

  if (isNBP && !isLocation) {
    return false;
  }

  return true;
};

export const itemSearchFilter = (
  item: LocationListItemsType,
  searchLower: string,
  includeNonLocations = false,
) => {
  const searchable = isSearchable(item);

  if (!searchable && !includeNonLocations) {
    return false;
  }

  const description = (item.meta?.description ?? '').toLowerCase();
  const fullName = (item.altLabel ?? item.label).toLowerCase();
  const value = item.value.toLowerCase();

  const searchQueryParts = searchLower.split(' ').filter(Boolean);

  for (const queryPart of searchQueryParts) {
    const matchesAnyField =
      description.includes(queryPart) || fullName.includes(queryPart) || value.includes(queryPart);

    if (!matchesAnyField) {
      return false;
    }
  }

  return true;
};

export const filterItems = (
  items: LocationListItemsType[],
  searchLower: string,
): LocationListItemsType[] => {
  const filteredItems: LocationListItemsType[] = [];

  for (const item of items) {
    const isInSearch = itemSearchFilter(item, searchLower);

    if (isInSearch) {
      filteredItems.push({ ...item }); //Include this and all its children
    } else {
      const filteredChildren = filterItems(item.children, searchLower);

      if (filteredChildren.length > 0) {
        filteredItems.push({ ...item, children: filteredChildren });
      }
    }
  }

  return filteredItems;
};

// Flatten items to one level
const flattenItems = (items: LocationListItemsType[]): LocationListItemsType[] => {
  const flatItems: LocationListItemsType[] = [];

  for (const item of items) {
    if (item.type === 'value') flatItems.push(item);

    if (item.children.length > 0) {
      // eslint-disable-next-line prefer-spread -- Bad performance
      flatItems.push.apply(flatItems, flattenItems(item.children));
    }
  }

  return flatItems;
};

const locationSearchItemsFlat = (items: LocationListItemsType[], search: string) => {
  const flatItems = flattenItems(items);

  // Remove children
  const flatNoChilds = flatItems.map((item) => {
    const newItem = { ...item, children: [] };

    if (item.meta) {
      newItem.meta = { ...item.meta, childCount: 0 };
    }

    return newItem;
  });

  return filterItems(flatNoChilds, search.toLowerCase());
};

export const filterLocationItems = (
  inputItems: LocationListItemsType[],
  search: string,
  isMultiSelect: boolean,
): LocationListItemsType[] => {
  const searchLower = search.toLowerCase();
  const items = [...inputItems];

  if (!isMultiSelect) {
    const fnoItems = items.filter((item) => isDatalakeRecord(item.meta?.source));
    let fnoResults: LocationListItemsType[] = [];

    if (fnoItems.length > 0) {
      fnoResults = locationSearchItemsFlat(fnoItems, searchLower).map(
        convertListItemToFullNameItem,
      );
    }

    const allExceptFNO = items.filter((item) => !isDatalakeRecord(item.meta?.source));

    const filteredItems = locationSearchItemsFlat(allExceptFNO, searchLower);

    return [...fnoResults, ...filteredItems];
  }

  return filterItems(items, searchLower);
};

const convertListItemToFullNameItem = (item: LocationListItemsType): LocationListItemsType => {
  const fullName = item.altLabel ?? item.label;

  return {
    ...item,
    label: fullName,
  };
};
